/**
 * Planar polygon helpers: plane fitting, 2d projection, ear-clipping
 * triangulation and point-in-polygon tests. Outlines may be concave but must
 * not self-intersect. Points are flat xyz triplets; 2d points flat xy pairs.
 */

type Plane = {
    // orthonormal basis: origin o, in-plane axes u/v, normal n
    o: [number, number, number];
    u: [number, number, number];
    v: [number, number, number];
    n: [number, number, number];
};

// fit a plane through an ordered outline using Newell's method (robust for
// concave outlines). falls back to a covariance-free axis pick when the
// outline is degenerate.
const fitPlane = (points: number[]): Plane | null => {
    const n = points.length / 3;
    if (n < 3) {
        return null;
    }

    const o: [number, number, number] = [0, 0, 0];
    for (let i = 0; i < n; i++) {
        o[0] += points[i * 3];
        o[1] += points[i * 3 + 1];
        o[2] += points[i * 3 + 2];
    }
    o[0] /= n;
    o[1] /= n;
    o[2] /= n;

    // Newell normal
    let nx = 0;
    let ny = 0;
    let nz = 0;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        const ax = points[i * 3];
        const ay = points[i * 3 + 1];
        const az = points[i * 3 + 2];
        const bx = points[j * 3];
        const by = points[j * 3 + 1];
        const bz = points[j * 3 + 2];
        nx += (ay - by) * (az + bz);
        ny += (az - bz) * (ax + bx);
        nz += (ax - bx) * (ay + by);
    }
    const len = Math.hypot(nx, ny, nz);
    if (len < 1e-12) {
        return null;  // collinear outline
    }
    const normal: [number, number, number] = [nx / len, ny / len, nz / len];

    // in-plane basis: pick the world axis least aligned with the normal
    const pick: [number, number, number] = Math.abs(normal[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
    const u: [number, number, number] = [
        pick[1] * normal[2] - pick[2] * normal[1],
        pick[2] * normal[0] - pick[0] * normal[2],
        pick[0] * normal[1] - pick[1] * normal[0]
    ];
    const ulen = Math.hypot(u[0], u[1], u[2]);
    u[0] /= ulen;
    u[1] /= ulen;
    u[2] /= ulen;
    const v: [number, number, number] = [
        normal[1] * u[2] - normal[2] * u[1],
        normal[2] * u[0] - normal[0] * u[2],
        normal[0] * u[1] - normal[1] * u[0]
    ];

    return { o, u, v, n: normal };
};

// project 3d points onto the plane basis -> flat xy pairs
const projectToPlane = (points: number[], plane: Plane): number[] => {
    const n = points.length / 3;
    const out = new Array(n * 2);
    for (let i = 0; i < n; i++) {
        const dx = points[i * 3] - plane.o[0];
        const dy = points[i * 3 + 1] - plane.o[1];
        const dz = points[i * 3 + 2] - plane.o[2];
        out[i * 2] = dx * plane.u[0] + dy * plane.u[1] + dz * plane.u[2];
        out[i * 2 + 1] = dx * plane.v[0] + dy * plane.v[1] + dz * plane.v[2];
    }
    return out;
};

const unprojectFromPlane = (x: number, y: number, plane: Plane): [number, number, number] => {
    return [
        plane.o[0] + x * plane.u[0] + y * plane.v[0],
        plane.o[1] + x * plane.u[1] + y * plane.v[1],
        plane.o[2] + x * plane.u[2] + y * plane.v[2]
    ];
};

const signedArea2d = (pts2d: number[]): number => {
    const n = pts2d.length / 2;
    let area = 0;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += pts2d[i * 2] * pts2d[j * 2 + 1] - pts2d[j * 2] * pts2d[i * 2 + 1];
    }
    return area / 2;
};

// crossing-number point-in-polygon test on the outline
const insidePolygon2d = (pts2d: number[], x: number, y: number): boolean => {
    const n = pts2d.length / 2;
    let inside = false;
    for (let i = 0, j = n - 1; i < n; j = i++) {
        const xi = pts2d[i * 2];
        const yi = pts2d[i * 2 + 1];
        const xj = pts2d[j * 2];
        const yj = pts2d[j * 2 + 1];
        if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
            inside = !inside;
        }
    }
    return inside;
};

// ear-clipping triangulation of a simple (possibly concave) outline.
// returns index triples into the outline, ccw relative to the outline plane.
const triangulate = (pts2d: number[]): number[] => {
    const count = pts2d.length / 2;
    if (count < 3) {
        return [];
    }

    // work on a ccw copy of the index list
    const indices: number[] = [];
    for (let i = 0; i < count; i++) {
        indices.push(i);
    }
    if (signedArea2d(pts2d) < 0) {
        indices.reverse();
    }

    const x = (i: number) => pts2d[i * 2];
    const y = (i: number) => pts2d[i * 2 + 1];
    const areaOf = (a: number, b: number, c: number) => {
        return (x(b) - x(a)) * (y(c) - y(a)) - (x(c) - x(a)) * (y(b) - y(a));
    };
    const inTriangle = (a: number, b: number, c: number, p: number) => {
        const d1 = areaOf(a, b, p);
        const d2 = areaOf(b, c, p);
        const d3 = areaOf(c, a, p);
        return d1 >= 0 && d2 >= 0 && d3 >= 0;
    };

    const triangles: number[] = [];
    let guard = 0;
    while (indices.length > 3 && guard++ < 10000) {
        let clipped = false;
        for (let i = 0; i < indices.length; i++) {
            const a = indices[(i + indices.length - 1) % indices.length];
            const b = indices[i];
            const c = indices[(i + 1) % indices.length];

            if (areaOf(a, b, c) <= 0) {
                continue;  // reflex corner
            }

            let ear = true;
            for (const p of indices) {
                if (p !== a && p !== b && p !== c && inTriangle(a, b, c, p)) {
                    ear = false;
                    break;
                }
            }
            if (ear) {
                triangles.push(a, b, c);
                indices.splice(i, 1);
                clipped = true;
                break;
            }
        }
        if (!clipped) {
            break;  // degenerate/self-intersecting outline: stop gracefully
        }
    }
    if (indices.length === 3) {
        triangles.push(indices[0], indices[1], indices[2]);
    }
    return triangles;
};

// 2d convex hull (monotone chain) -> ordered outline indices, ccw
const convexHull2d = (pts2d: number[]): number[] => {
    const n = pts2d.length / 2;
    const idx: number[] = [];
    for (let i = 0; i < n; i++) {
        idx.push(i);
    }
    idx.sort((a, b) => (pts2d[a * 2] - pts2d[b * 2]) || (pts2d[a * 2 + 1] - pts2d[b * 2 + 1]));

    const crossZ = (o: number, a: number, b: number) => {
        return (pts2d[a * 2] - pts2d[o * 2]) * (pts2d[b * 2 + 1] - pts2d[o * 2 + 1]) -
               (pts2d[a * 2 + 1] - pts2d[o * 2 + 1]) * (pts2d[b * 2] - pts2d[o * 2]);
    };

    const lower: number[] = [];
    for (const i of idx) {
        while (lower.length >= 2 && crossZ(lower[lower.length - 2], lower[lower.length - 1], i) <= 0) {
            lower.pop();
        }
        lower.push(i);
    }
    const upper: number[] = [];
    for (let k = idx.length - 1; k >= 0; k--) {
        const i = idx[k];
        while (upper.length >= 2 && crossZ(upper[upper.length - 2], upper[upper.length - 1], i) <= 0) {
            upper.pop();
        }
        upper.push(i);
    }
    lower.pop();
    upper.pop();
    return lower.concat(upper);
};

export { Plane, fitPlane, projectToPlane, unprojectFromPlane, signedArea2d, insidePolygon2d, triangulate, convexHull2d };
